<template>
  <div>
    <b-tabs v-model="tabIndex">
      <b-tab>
        <template #title>
          <feather-icon
            icon="UserIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Details</span>
        </template>
        <b-overlay
          variant="white"
          spinner-variant="primary"
          blur=""
          opacity="1"
          rounded="sm"
          :show="fetchingUser"
        >
          <user-details
            v-if="userDetails && !fetchingUser"
            :details="userDetails"
            :pic="userProfilePic"
          />
        </b-overlay>
      </b-tab>
      <b-tab v-if="$can('read', 'user-wallet-settings') || $can('read', 'user-wallet-transactions')">
        <template #title>
          <feather-icon
            icon="BriefcaseIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Wallet</span>
        </template>
        <sticky-user-details
          viewd-in="user-wallet"
          :user-details="userDetails"
          :loading="loading"
          @refetch-user-wallet="refetchWallet"
        />
        <user-wallet-info
          :wallet-loading="loading"
          :wallet-details="walletDetails"
          @refetch-balance="fetchUserWallet"
        />
        <user-card-details
          :payment-methods-data="userPaymentMethods"
          :loading-payment-methods="userPaymentMethodsLoading"
        />
        <user-transactions
          :transactions-data="userTransactions"
          :loading-transactions="transactionsLoading"
          @refetch-user-transactions="fetchUserTransactions($event)"
          @refetch-user-wallet="refetchWallet('transactions')"
        />
        <user-pending-transactions
          :loading-pending-transactions="pendingTransactionsLoading"
          :pending-transactions-data="userPendingTransactions"
          @refetch-user-pending-transactions="fetchUserPendingTransactions($event)"
          @refetch-user-wallet="refetchWallet('pendingTransactions')"
        />
        <UserVisaTransactions
          v-if="$can('read', 'visa-transactions')"
          :loading-visa-transactions="visaTransactionsLoading"
          :visa-transactions-data="userVisaTransactions"
          @fetch-visa-transactions="fetchVisaTransactions"
          @fetch-wallet="refetchWallet('visa')"
        />
      </b-tab>

      <b-tab v-if="$can('read', 'user-notes')">
        <template #title>
          <feather-icon
            icon="EditIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Notes</span>
        </template>
        <sticky-user-details
          :user-details="userDetails"
          :loading="loading"
        />
        <notes-list notable-type="user" />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="TruckIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Trips History</span>
        </template>
        <sticky-user-details
          :user-details="userDetails"
          :loading="loading"
        />
        <driver-user-trips
          type="user"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="TruckIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Booking History</span>
        </template>
        <sticky-user-details
          :user-details="userDetails"
          :loading="loading"
        />
        <BookingHistoryTab />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="PhoneIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">Support</span>
        </template>
        <support-tab
          user-type="user"
          :user-external-id="externalUserId"
        />
      </b-tab>
      <b-tab>
        <template #title>
          <feather-icon
            icon="ClockIcon"
            size="16"
            class="mr-0 mr-sm-50"
          />
          <span class="d-none d-sm-inline">ETA by Service</span>
        </template>
        <sticky-user-details
          :user-details="userDetails"
          :loading="loading"
        />
        <eta-by-service-tab />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { BTabs, BTab, BOverlay } from 'bootstrap-vue'
import useUserDetails from '../hooks/useUserDetails'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import storeModule from '../store'
import transactionTypesStore from '@/views/billing/transaction-types/transactionTypesStore'
import UserDetails from '../components/details-tab/UserDetails.vue'
import UserWalletInfo from '../components/wallet-tab/UserWalletInfo.vue'
import UserTransactions from '../components/wallet-tab/UserTransactions.vue'
import UserPendingTransactions from '../components/wallet-tab/UserPendingTransactions.vue'
import UserVisaTransactions from '../components/wallet-tab/UserVisaTransactions.vue'
import UserCardDetails from '../components/wallet-tab/UserCardDetails.vue'
import StickyUserDetails from '../components/StickyUserDetails.vue'
import NotesList from '@/views/driver-user-shared/notes/driver-user-notes-tab/NotesList.vue'
import DriverUserTrips from '@/views/driver-user-shared/driver-user-trips/components/DriverUserTrips.vue'
import SupportTab from '@/views/driver-user-shared/support/SupportTab.vue'
import billingStore from '@/views/billing/billingStore'
import BookingHistoryTab from '../components/details-tab/BookingHistoryTab.vue'
import EtaByServiceTab from '../components/eta-by-service-tab/EtaByServiceTab.vue'

export default {
  components: {
    UserDetails,
    UserWalletInfo,
    StickyUserDetails,
    UserPendingTransactions,
    UserTransactions,
    UserVisaTransactions,
    UserCardDetails,
    BTabs,
    BookingHistoryTab,
    BTab,
    BOverlay,
    NotesList,
    DriverUserTrips,
    SupportTab,
    EtaByServiceTab,
  },
  setup(_, ctx) {
    useRegisterStoreModule('users', storeModule)
    useRegisterStoreModule('transactionTypes', transactionTypesStore)
    useRegisterStoreModule('billing', billingStore)

    const {
      fetchingUser, fetchUserTransactions,
      userProfilePic,
      transactionsLoading,
      userTransactions,
      userPendingTransactions,
      pendingTransactionsLoading,
      fetchUserPendingTransactions,
      fetchVisaTransactions,
      visaTransactionsLoading,
      userVisaTransactions,
      walletDetails,
      userDetails,
      fetchUserDetails,
      fetchUserWallet,
      loading,
      tabIndex,
      externalUserId,
      userPaymentMethodsLoading,
      fetchPaymentMethods,
      userPaymentMethods,
    } = useUserDetails(ctx)

    const refetchWallet = type => {
      if (type === 'transactions') {
        fetchUserTransactions()
      } else if (type === 'visa') {
        fetchVisaTransactions()
      } else {
        fetchUserPendingTransactions()
      }
      fetchUserWallet()
    }

    return {
      userProfilePic,
      fetchingUser,
      fetchUserTransactions,
      transactionsLoading,
      userTransactions,
      userPendingTransactions,
      pendingTransactionsLoading,
      fetchUserPendingTransactions,
      fetchVisaTransactions,
      visaTransactionsLoading,
      userVisaTransactions,
      walletDetails,
      userDetails,
      fetchUserDetails,
      fetchUserWallet,
      loading,
      refetchWallet,
      tabIndex,
      externalUserId,
      userPaymentMethodsLoading,
      fetchPaymentMethods,
      userPaymentMethods,
    }
  },
}
</script>

<style>

</style>
